import { Phone, PhoneIncoming, PhoneOff, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useCall } from '@/hooks/useCalling'
import { useRealtimeStore } from '@/hooks/useRealtime'
import { useCallStore } from '@/stores/call.store'
import type { Call } from '@/types/api/calling'
import { cn } from '@/utils/cn'
import { isNativeApp } from '@/utils/platform'

import { callStatusText } from '../callText'
import { useCallRealtimeSync } from '../hooks/useCallRealtimeSync'

import { DispositionDialog } from './DispositionDialog'

/** Floating chips for the user's live and just-ended calls, mounted once in the app shell. */
export function ActiveCallDock() {
  useCallRealtimeSync()
  const activeCallIds = useCallStore((s) => s.activeCallIds)
  const dispositionCallId = useCallStore((s) => s.dispositionCallId)

  return (
    <>
      {activeCallIds.length > 0 && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-6 sm:items-end"
          aria-live="polite"
        >
          {activeCallIds.map((callId) => (
            <CallChip key={callId} callId={callId} />
          ))}
        </div>
      )}
      {dispositionCallId && <DispositionDialog callId={dispositionCallId} />}
    </>
  )
}

function useNow(active: boolean) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [active])
  return now
}

function CallChip({ callId }: { callId: string }) {
  const connected = useRealtimeStore((s) => s.connected)
  // Without a live stream (or in the app, where SSE is off) poll the call while it is live.
  const { data: call } = useCall(callId, { pollWhileLive: !connected || isNativeApp() })
  const now = useNow(call?.status === 'CONNECTED')

  useEffect(() => {
    if (!call || call.status !== 'ENDED' || !call.canDispose || call.dispositionId) return
    // A call that never got going (provider error, canceled push) needs no disposition prompt.
    if (call.outcome === 'FAILED' || call.outcome === 'CANCELED') return
    const { promptedDisposition, openDisposition } = useCallStore.getState()
    if (!promptedDisposition.includes(call.id)) openDisposition(call.id)
  }, [call])

  // Ended calls with nothing left to do leave the dock by themselves (it overlays page content).
  const settled = !!call && call.status === 'ENDED' && (!call.canDispose || !!call.dispositionId
    || call.outcome === 'FAILED' || call.outcome === 'CANCELED')
  useEffect(() => {
    if (!settled || !call) return
    const timer = setTimeout(() => useCallStore.getState().dismissCall(call.id), call.dispositionId ? 3_000 : 15_000)
    return () => clearTimeout(timer)
  }, [settled, call])

  if (!call) return null
  const ended = call.status === 'ENDED'

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-2xl border bg-white px-3 py-2.5 shadow-lg',
        ended ? 'border-neutral-200' : 'border-lime-300'
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white',
          ended ? 'bg-neutral-400' : call.status === 'CONNECTED' ? 'bg-emerald-500' : 'animate-pulse bg-lime-500'
        )}
      >
        {ended ? <PhoneOff className="h-4 w-4" /> : call.direction === 'INBOUND' ? <PhoneIncoming className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <LeadName call={call} />
        <p className="truncate text-xs text-neutral-500 tabular-nums">{callStatusText(call, now)}</p>
      </div>
      {ended && call.canDispose && !call.dispositionId && (
        <button
          type="button"
          className="rounded-lg bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-700 hover:bg-primary-100"
          onClick={() => useCallStore.getState().openDisposition(call.id)}
        >
          Disposition
        </button>
      )}
      {ended && (
        <button
          type="button"
          className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
          aria-label="Dismiss"
          onClick={() => useCallStore.getState().dismissCall(call.id)}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

function LeadName({ call }: { call: Call }) {
  const name = call.leadName ?? call.leadNumber ?? 'Unknown caller'
  if (call.recordId && call.recordGroupId) {
    return (
      <Link
        to={`/record-groups/${call.recordGroupId}/records/${call.recordId}`}
        className="block truncate text-sm font-semibold text-neutral-900 hover:underline"
      >
        {name}
      </Link>
    )
  }
  return <p className="truncate text-sm font-semibold text-neutral-900">{name}</p>
}
